/**
 * Admin → user detail drawer. Right-side OverlayDrawer with four tabs:
 *   Profile   — editable (role/FA/FA-role/active/test/visible/bio/github) +
 *               read-only SSO/Graph fields + Approve action
 *   Apps      — the user's applications (GET /applications?applicant_id)
 *   Activity  — notifications they received (GET /admin/activity?user_id)
 *   History   — admin actions that targeted them (GET /admin/audit-log?target_user_id)
 */

import { useEffect, useState } from 'react'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Divider,
  Dropdown,
  Field,
  Input,
  OverlayDrawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Option,
  Spinner,
  Switch,
  Tab,
  TabList,
  Textarea,
  tokens,
} from '@fluentui/react-components'
import { Dismiss24Regular } from '@fluentui/react-icons'
import { FOCUS_AREAS } from '../types/focus_area'
import type { AdminUserDetail, AdminUserUpdate, UserRoleStr } from '../types/admin'
import {
  useUpdateUser,
  useUserActivity,
  useUserApplications,
  useUserAuditLog,
  useUserDetail,
} from '../hooks/useAdminUsers'
import { useToast } from './ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { githubHandleProblem } from '../lib/githubHandle'

const ROLES: { value: UserRoleStr; label: string }[] = [
  { value: 'talent', label: 'Talent' },
  { value: 'council', label: 'Council' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
]
const FA_ROLES = [
  { value: '', label: '—' },
  { value: 'lead', label: 'Lead' },
  { value: 'coach', label: 'Coach' },
]
const NO_FA = '__none__'

function errMsg(e: unknown, fallback: string): string {
  return apiErrorMessage(e, fallback)
}

const fmtDate = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : '—')

function summarize(detail: Record<string, unknown>): string {
  return Object.entries(detail || {})
    .map(([k, v]) =>
      v && typeof v === 'object' && 'to' in (v as object)
        ? `${k}: ${(v as { from?: unknown }).from ?? '∅'} → ${(v as { to?: unknown }).to ?? '∅'}`
        : `${k}: ${String(v)}`,
    )
    .join(' · ')
}

interface UserDetailDrawerProps {
  userId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UserDetailDrawer({ userId, open, onOpenChange }: UserDetailDrawerProps) {
  const { showToast } = useToast()
  const [tab, setTab] = useState('profile')
  const { data: u, isLoading } = useUserDetail(open ? userId : null)
  const updateUser = useUpdateUser()

  // Editable local state, seeded from the fetched detail.
  const [role, setRole] = useState<UserRoleStr>('talent')
  const [fa, setFa] = useState(NO_FA)
  const [faRole, setFaRole] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [isTest, setIsTest] = useState(false)
  const [visible, setVisible] = useState(true)
  const [viewsAll, setViewsAll] = useState(false)
  const [bio, setBio] = useState('')
  const [github, setGithub] = useState('')

  useEffect(() => {
    if (!u) return
    setRole(u.role)
    setFa(u.focus_area_code || NO_FA)
    setFaRole(u.focus_area_role || '')
    setIsActive(u.is_active)
    setIsTest(u.is_test_account)
    setVisible(u.visible_in_directory)
    setViewsAll(u.views_all_demands)
    setBio(u.bio || '')
    setGithub(u.github_handle || '')
    setTab('profile')
  }, [u])

  function buildChange(d: AdminUserDetail): AdminUserUpdate {
    const c: AdminUserUpdate = {}
    if (role !== d.role) c.role = role
    const faVal = fa === NO_FA ? '' : fa
    if (faVal !== (d.focus_area_code || '')) c.focus_area_code = faVal
    if (faRole !== (d.focus_area_role || '')) c.focus_area_role = faRole
    if (isActive !== d.is_active) c.is_active = isActive
    if (isTest !== d.is_test_account) c.is_test_account = isTest
    if (visible !== d.visible_in_directory) c.visible_in_directory = visible
    if (viewsAll !== d.views_all_demands) c.views_all_demands = viewsAll
    if (bio !== (d.bio || '')) c.bio = bio
    if (github !== (d.github_handle || '')) c.github_handle = github
    return c
  }

  function save(change: AdminUserUpdate, label: string) {
    if (!u) return
    updateUser.mutate(
      { id: u.id, change },
      {
        onSuccess: () => showToast({ title: label, intent: 'success' }),
        onError: (e) => showToast({ title: 'Failed', body: errMsg(e, label), intent: 'error' }),
      },
    )
  }

  return (
    <OverlayDrawer
      open={open}
      onOpenChange={(_, d) => onOpenChange(d.open)}
      position="end"
      size="medium"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              aria-label="Close"
              onClick={() => onOpenChange(false)}
            />
          }
        >
          {u ? u.name || u.email : 'User'}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {isLoading || !u ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Spinner size="small" />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <Badge appearance="outline">{u.role}</Badge>
              {u.is_approved === false && (
                <Badge appearance="tint" color="warning">
                  PENDING
                </Badge>
              )}
              {!u.is_active && (
                <Badge appearance="tint" color="danger">
                  INACTIVE
                </Badge>
              )}
              {u.is_test_account && (
                <Badge appearance="tint" color="warning">
                  TEST
                </Badge>
              )}
            </div>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{u.email}</Caption1>

            <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as string)}>
              <Tab value="profile">Profile</Tab>
              <Tab value="apps">Applications</Tab>
              <Tab value="activity">Activity</Tab>
              <Tab value="history">History</Tab>
            </TabList>

            {tab === 'profile' && (
              <ProfileTab
                u={u}
                role={role}
                setRole={setRole}
                fa={fa}
                setFa={setFa}
                faRole={faRole}
                setFaRole={setFaRole}
                isActive={isActive}
                setIsActive={setIsActive}
                isTest={isTest}
                setIsTest={setIsTest}
                visible={visible}
                setVisible={setVisible}
                viewsAll={viewsAll}
                setViewsAll={setViewsAll}
                bio={bio}
                setBio={setBio}
                github={github}
                setGithub={setGithub}
                pending={updateUser.isPending}
                onSave={() => {
                  const c = buildChange(u)
                  if (Object.keys(c).length) save(c, 'Saved')
                }}
                onApprove={() => save({ is_approved: true }, `Approved ${u.name}`)}
                dirty={Object.keys(buildChange(u)).length > 0}
              />
            )}
            {tab === 'apps' && <AppsTab userId={u.id} />}
            {tab === 'activity' && <ActivityTab userId={u.id} />}
            {tab === 'history' && <HistoryTab userId={u.id} />}
          </div>
        )}
      </DrawerBody>
    </OverlayDrawer>
  )
}

interface ProfileTabProps {
  u: AdminUserDetail
  role: UserRoleStr
  setRole: (r: UserRoleStr) => void
  fa: string
  setFa: (s: string) => void
  faRole: string
  setFaRole: (s: string) => void
  isActive: boolean
  setIsActive: (b: boolean) => void
  isTest: boolean
  setIsTest: (b: boolean) => void
  visible: boolean
  setVisible: (b: boolean) => void
  viewsAll: boolean
  setViewsAll: (b: boolean) => void
  bio: string
  setBio: (s: string) => void
  github: string
  setGithub: (s: string) => void
  pending: boolean
  dirty: boolean
  onSave: () => void
  onApprove: () => void
}

function ProfileTab(p: ProfileTabProps) {
  const { u } = p
  const ro = (label: string, value: string | null) => (
    <div>
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{label}</Caption1>
      <Body1>{value || '—'}</Body1>
    </div>
  )
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
      {u.is_approved === false && (
        <Button appearance="primary" disabled={p.pending} onClick={p.onApprove}>
          Approve user
        </Button>
      )}

      <Field label="Role">
        <Dropdown
          value={ROLES.find((r) => r.value === p.role)?.label || p.role}
          selectedOptions={[p.role]}
          onOptionSelect={(_, d) => p.setRole((d.optionValue as UserRoleStr) ?? p.role)}
        >
          {ROLES.map((r) => (
            <Option key={r.value} value={r.value}>
              {r.label}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Field label="Focus area">
        <Dropdown
          value={p.fa === NO_FA ? '—' : FOCUS_AREAS.find((f) => f.code === p.fa)?.name || p.fa}
          selectedOptions={[p.fa]}
          onOptionSelect={(_, d) => p.setFa((d.optionValue as string) ?? NO_FA)}
        >
          <Option value={NO_FA}>—</Option>
          {FOCUS_AREAS.map((f) => (
            <Option key={f.code} value={f.code} text={`${f.code} · ${f.name}`}>
              {f.code} · {f.name}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Field label="FA role">
        <Dropdown
          value={FA_ROLES.find((r) => r.value === p.faRole)?.label || '—'}
          selectedOptions={[p.faRole]}
          onOptionSelect={(_, d) => p.setFaRole((d.optionValue as string) ?? '')}
        >
          {FA_ROLES.map((r) => (
            <Option key={r.value || 'none'} value={r.value}>
              {r.label}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Switch checked={p.isActive} onChange={(_, d) => p.setIsActive(d.checked)} label="Active" />
      <Switch
        checked={p.visible}
        onChange={(_, d) => p.setVisible(d.checked)}
        label="Visible in directory"
      />
      <Switch checked={p.isTest} onChange={(_, d) => p.setIsTest(d.checked)} label="Test account" />
      {/* #18: leadership review the whole pipeline but sit on no council. This
          grants READ across every focus area — including the council-only
          strategic fields — and no write at all. Kept next to the other
          switches rather than beside Role, because it is deliberately NOT a
          role: a council role would also subscribe them to the weekly digest. */}
      <Switch
        checked={p.viewsAll}
        onChange={(_, d) => p.setViewsAll(d.checked)}
        label="Read all focus areas (no editing)"
      />
      <Field label="Bio">
        <Textarea value={p.bio} onChange={(_, d) => p.setBio(d.value)} rows={3} resize="vertical" />
      </Field>
      <Field label="GitHub handle" validationMessage={githubHandleProblem(p.github) ?? undefined}>
        <Input value={p.github} onChange={(_, d) => p.setGithub(d.value)} />
      </Field>

      <Button
        appearance="primary"
        disabled={!p.dirty || p.pending || !!githubHandleProblem(p.github)}
        onClick={p.onSave}
      >
        {p.pending ? 'Saving…' : 'Save changes'}
      </Button>

      <Divider />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingVerticalS }}>
        {ro('Job title', u.job_title)}
        {ro('Department', u.department)}
        {ro('Company', u.company)}
        {ro('Country', u.country)}
        {ro('SSO linked', u.azure_id ? 'yes' : 'not yet')}
        {ro('Created', fmtDate(u.created_at))}
      </div>
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return (
    <Caption1 style={{ color: tokens.colorNeutralForeground3, padding: '12px 0' }}>{text}</Caption1>
  )
}

function AppsTab({ userId }: { userId: number }) {
  const { data, isLoading } = useUserApplications(userId)
  if (isLoading) return <Spinner size="tiny" />
  if (!data || data.length === 0) return <Empty text="No applications." />
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((a) => (
        <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Body1>{a.demand_title || `Demand #${a.demand_id ?? '?'}`}</Body1>
          <Badge appearance="tint">{a.status || '—'}</Badge>
        </div>
      ))}
    </div>
  )
}

function ActivityTab({ userId }: { userId: number }) {
  const { data, isLoading } = useUserActivity(userId)
  if (isLoading) return <Spinner size="tiny" />
  if (!data || data.length === 0) return <Empty text="No activity." />
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((n) => (
        <div key={n.id}>
          <Body1>{n.message}</Body1>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            {n.type} · {fmtDate(n.created_at)}
          </Caption1>
        </div>
      ))}
    </div>
  )
}

function HistoryTab({ userId }: { userId: number }) {
  const { data, isLoading } = useUserAuditLog(userId)
  if (isLoading) return <Spinner size="tiny" />
  if (!data || data.length === 0) return <Empty text="No admin actions on this user." />
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((r) => (
        <div key={r.id}>
          <Body1>{summarize(r.detail) || r.action}</Body1>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            {r.actor_name || 'system'} · {fmtDate(r.created_at)}
          </Caption1>
        </div>
      ))}
    </div>
  )
}
